/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useState } from 'react';
import { filterMenuStyles } from './filter-menu.styles';
import closeIcon from '../../../../../assets/icons/chevron_left.svg';
import { Switch, useMediaQuery } from '@mui/material';
import { EFilters } from '../constants';
import PredefinedSwipableDrawer from '../../../../../components/swipable-edge-drawer/swipable-drawer.component';
import { Z_INDEX } from '../../../../../constants/zIndex';
import ConnectorTypeFilter from '../connector-type-filter/connector-type-filter.component';
import Power from '../power/power.component';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocationsParams } from '../../../../../store/locations/locations-list.selector';
import {
    resetLocationsParams,
    setLocationsParamsCanadianMadeOnly,
    setLocationsParamsIsAvailable,
    setLocationsParamsIsFree,
} from '../../../../../store/locations/locations-list.slice';
import { BooleanParam, NumberParam, useQueryParams } from 'use-query-params';
import UnitsFilter from '../units-filter/units-filter';
import PricingFilter from '../pricing-filter/pricing-filter';
import { useIntl } from 'react-intl';

type Props = {
    onClose: () => void;
    onApply: (data: any) => void;
};

const FilterMenu = (props: Props) => {
    const { onClose, onApply } = props;
    const isDesktop = useMediaQuery('(min-width: 600px)');
    const [modalIsOpen, setModalIsOpen] = useState(true);
    const dispatch = useDispatch();
    const intl = useIntl();
    const [query, setQuery] = useQueryParams({
        isFree: BooleanParam,
        canadianMadeOnly: BooleanParam,
        isConnectorAvailable: BooleanParam,
        minPower: NumberParam,
        maxPower: NumberParam,
    });

    useEffect(() => {
        if (query.isFree) {
            dispatch(setLocationsParamsIsFree(query.isFree));
        }
        if (query.canadianMadeOnly) {
            dispatch(setLocationsParamsCanadianMadeOnly(query.canadianMadeOnly));
        }
    }, [query.isFree, query.canadianMadeOnly]);

    const searchFilters = useSelector(selectLocationsParams);

    const onReset = () => {
        dispatch(resetLocationsParams());
        setQuery({
            isFree: undefined,
            canadianMadeOnly: undefined,
            isConnectorAvailable: undefined,
            minPower: undefined,
            maxPower: undefined,
        });
    };

    const isCanadianActive = useMemo(() => {
        return searchFilters.canadianMadeOnly;
    }, [searchFilters.canadianMadeOnly]);

    const changeFilter = (filter: EFilters) => () => onApply(filter);

    const onCloseDrawer = () => {
        setModalIsOpen(false);
        onClose();
    };

    const changeAvailableFilter = () => {
        const currentValue = !!searchFilters.isConnectorAvailable;
        dispatch(setLocationsParamsIsAvailable(!currentValue));
        setQuery({ isConnectorAvailable: !currentValue ? true : undefined });
    };

    const changeCanadianFilter = () => {
        const currentValue = searchFilters.canadianMadeOnly;
        dispatch(setLocationsParamsCanadianMadeOnly(!currentValue));
        setQuery({ canadianMadeOnly: !currentValue ? true : undefined });
    };

    const content = useMemo(
        () => (
            <div css={filterMenuStyles.root}>
                <div css={filterMenuStyles.headerWrapper}>
                    <img
                        onClick={onClose}
                        css={filterMenuStyles.closeIcon}
                        src={closeIcon}
                        alt="close"
                    />
                    <h3 css={filterMenuStyles.header}>{intl.formatMessage({ id: 'filters' })}</h3>
                    <span css={filterMenuStyles.reset} onClick={onReset}>
                        {intl.formatMessage({ id: 'reset' })}
                    </span>
                </div>
                <ConnectorTypeFilter />
                <Power />
                <PricingFilter />
                <div css={filterMenuStyles.filtersWrapper}>
                    <span css={filterMenuStyles.filterItemLabel}>
                        {intl.formatMessage({ id: 'showAvailableOnly' })}
                    </span>
                    <Switch
                        checked={!!searchFilters.isConnectorAvailable}
                        onChange={changeAvailableFilter}
                    />
                </div>
                <div
                    css={
                        isCanadianActive
                            ? [
                                  filterMenuStyles.filtersWrapper,
                                  filterMenuStyles.canadianWrapperActive,
                              ]
                            : filterMenuStyles.filtersWrapper
                    }>
                    <span css={filterMenuStyles.filterItemLabel}>
                        {intl.formatMessage({ id: 'canadianMadeChargersOnly' })}
                    </span>
                    <Switch
                        checked={searchFilters.canadianMadeOnly}
                        onChange={changeCanadianFilter}
                    />
                </div>
                <UnitsFilter />
            </div>
        ),
        [isCanadianActive, onReset, onClose, changeFilter, searchFilters.isConnectorAvailable]
    );

    if (isDesktop) {
        return content;
    }

    return (
        <PredefinedSwipableDrawer
            onClose={onCloseDrawer}
            onOpen={() => setModalIsOpen(true)}
            isOpen={modalIsOpen}
            openDrawerHeight="95%"
            zIndex={Z_INDEX.DRAWER_FILTER}>
            {content}
        </PredefinedSwipableDrawer>
    );
};

export default FilterMenu;
